import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import ClientFinalSignatureView from '../../components/documents/ClientFinalSignatureView';
import useAuthStore from '../../context/authStore';

import BackButton from '../../components/common/BackButton';
/**
 * SignDocumentPage
 * Email signature link landing page
 * Allows authenticated users to sign documents via email link
 * Route: /documents/:id/sign
 */
export default function SignDocumentPage() {
  const { id: documentId } = useParams();
  const signingToken = new URLSearchParams(window.location.search).get('token') || '';
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [documentId, token, signingToken]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch document via public for-signing endpoint (no auth required)
      const qs = signingToken ? `?token=${encodeURIComponent(signingToken)}` : '';
      const res = await fetch(`/api/documents/${documentId}/for-signing${qs}`);

      if (!res.ok) {
        if (res.status === 404) {
          setError('Document not found');
        } else if (res.status === 403) {
          setError('You do not have permission to view this document. Please log in.');
        } else {
          setError('Failed to load document');
        }
        return;
      }

      const data = await res.json();
      setDocument(data.document || data);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeedback = async (feedbackPayload) => {
    try {
      const qs = signingToken ? `?token=${encodeURIComponent(signingToken)}` : '';
      const res = await fetch(`/api/documents/${documentId}/client-feedback${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...feedbackPayload,
          signingToken,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to add feedback');
      }

      // Refresh document to show new feedback
      await fetchDocument();
    } catch (err) {
      console.error('Failed to add feedback:', err);
      throw err;
    }
  };

  const handleSignDocument = async (signaturePayload) => {
    try {
      setSigning(true);

      const qs = signingToken ? `?token=${encodeURIComponent(signingToken)}` : '';
      const res = await fetch(`/api/documents/${documentId}/final-signature${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...signaturePayload,
          signingToken,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to sign document');
      }

      const data = await res.json();

      // Show success message
      if (data.certificate) {
        alert(`✅ Document signed successfully!\n\nCertificate #${data.certificate.certificateNumber}`);
      } else {
        alert('✅ Document signed successfully!');
      }

      // Redirect to signatures page or dashboard
      if (token && user) {
        navigate('/signatures');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to sign document:', err);
      throw err;
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 border border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="text-red-600" size={24} />
            <h1 className="text-xl font-semibold text-red-700">Error</h1>
          </div>
          <p className="text-secondary-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-secondary-500">Document not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Signed-in users can go back; signers from an email link have nowhere in the portal to return to */}
        {token && <BackButton fallbackTo="/signatures" className="mb-4" />}
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">Review & Sign Document</h1>
          <p className="text-secondary-600 mt-2">Document: {document.name}</p>
        </div>

        {/* Authentication notice for unauthenticated users */}
        {!token && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm">
              <strong>Note:</strong> You're viewing this document without logging in. 
              To sign documents, please <a href="/login" className="underline font-medium">log in</a> or create an account.
            </p>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <ClientFinalSignatureView
            document={document}
            clientName={user?.firstName ? `${user.firstName} ${user.lastName}` : document.clientName || ''}
            clientEmail={user?.email || document.clientEmail || ''}
            onAddFeedback={handleAddFeedback}
            onSignDocument={handleSignDocument}
            isLoading={signing}
          />
        </div>
      </div>
    </div>
  );
}
